/**
 * FunChallenges/types.ts
 *
 * Shared type vocabulary for the Fun Challenges feature in cc-web-learning.
 * Mirrors the wire shapes in:
 *   - cc-client-sdk/lib/schema.js  (funChallengeEnvelopeSchema)
 *   - cc-game-service/processor/funChallengeRoomProcessor.js
 *
 * Every BattleType value here must also be a Fun-Challenges gameType in
 * cc-game-service (e.g. "square_trainer" → handler at
 * cc-game-service/gameHandlers/funSquareTrainer.js).
 */

/** Game type sent on the wire (in `gameType` envelope field). */
export enum BattleType {
  // Must match the camel-cased filename in cc-game-service/gameHandlers/.
  // The server's loader does snake_case → camelCase, so 'fun_square_trainer'
  // resolves to gameHandlers/funSquareTrainer.js (the Fun Challenges handler
  // with generateSequence) — NOT gameHandlers/squareTrainer.js (NSCL's,
  // which has no generateSequence and would leave coords[] empty).
  SQUARE_TRAINER = 'fun_square_trainer',
  POSITION_TRAINER = 'fun_position_trainer',
  PUZZLE_DASH = 'fun_puzzle_dash',
  // Puzzle Royale — N-player (up to 100) timed puzzle race. Additive only;
  // its N-player state/shapes live in royaleTypes.ts, not here. Resolves to
  // cc-game-service/gameHandlers/funPuzzleRoyale.js.
  PUZZLE_ROYALE = 'fun_puzzle_royale',
  // Future: POWER_TRAINER = 'fun_power_trainer', etc.
}

/** Battle FSM as seen by `funChallengesBattleStore`. */
export type BattleStatus =
  | 'idle'
  | 'challenging'
  | 'incoming'
  | 'in_battle'
  | 'ended';

/** Tunables a player can set when starting a Fun Challenge. */
export interface ChallengeSettings {
  /** Total game duration in seconds. ST: 30/60/90. PTB: 60/120/180. */
  timerSec: number;
  /** Board orientation for both players (Square Trainer only). */
  orientation: 'white' | 'black' | 'random';
  /**
   * Position Trainer Battle mistake-out limit. Reaching this many wrong
   * puzzles ends the battle for that player (the OTHER player wins by
   * mistake_out). Ignored by Square Trainer. PTB chips: 3 / 5 / 10.
   * PDB also uses this with its own chips (3 / 5 / 7).
   */
  maxWrong?: number;
  /**
   * Puzzle Dash Battle target — first player to this many correct puzzles
   * wins by `target_reached`. PDB chips: 15 / 20 / 30. Ignored by ST + PTB.
   */
  targetCorrect?: number;
  /** Anything else the server-side handler might accept. */
  [key: string]: unknown;
}

/**
 * Snapshot of the active session, derived from `fun_session_start` and
 * `fun_session_rejoin` payloads.
 */
export interface BattleSession {
  battleId: string;            // sessionId from cc-game-service
  battleType: BattleType;
  myUserId: string;
  opponentId: string;
  opponentName?: string;
  /**
   * Server epoch ms when the 3-2-1 pre-game countdown finishes and moves
   * start being accepted. The visible timer ticks down from this boundary.
   * `0` (default) means there's no countdown — the battle is already live
   * from the moment the session was created. Older server builds that
   * don't send `startsAt` fall through to this default safely.
   */
  startsAt: number;
  /** Server epoch ms when the session timer fires (game over). */
  endsAt: number;
  settings: ChallengeSettings;
  /** Server-pre-rolled coord sequence; both players read from the same array. */
  coords: string[];
}

/** Aggregate scores tracked locally; updated on every fun_move_broadcast. */
export interface BattleScore {
  myCorrect: number;
  myWrong: number;
  opponentCorrect: number;
  opponentWrong: number;
}

/**
 * What we receive on `fun_room_invite`. The lobby's incoming-challenge modal
 * branches off this.
 */
export interface IncomingChallengePayload {
  roomId: string;
  battleType: BattleType;
  challengerId: string;
  challengerName: string;
  challengerAvatarUrl?: string;
  settings: ChallengeSettings;
}

/**
 * What we receive on `fun_session_end`. Persisted via
 * POST /fun_challenges/v1/square_trainer_results/.
 */
export interface BattleEndPayload {
  battleId: string;
  battleType: BattleType;
  winnerId: string;            // '' = draw
  rankedPlayers: string[];
  scores: Record<string, { correct: number; wrong: number }>;
  responses: Record<string, string[]>;
  reason: 'time_up' | 'forfeit' | 'mistake_out' | 'target_reached';
}

/** Code set the global listener understands. */
export type FunChallengeErrorCode =
  | 'INVITE_EXPIRED'
  | 'ROOM_CLOSED'
  | 'PLAYER_BUSY'
  | 'ROOM_NOT_FOUND'
  | 'ROOM_NOT_WAITING'
  | 'NO_ACTIVE_SESSION'
  | 'SESSION_NOT_FOUND'
  | 'SESSION_NOT_ACTIVE'
  | 'NOT_CREATOR'
  | 'NOT_ENOUGH_PLAYERS'
  | 'INVALID_PLAYER_COUNT'
  | 'UNKNOWN_GAME_TYPE'
  | 'CANNOT_LEAVE_ACTIVE_SESSION'
  | 'SEQUENCE_FETCH_FAILED'
  | 'SERVER_ERROR'
  | string; // permissive — server may evolve codes

/** Payload of `fun_error`. */
export interface FunChallengeError {
  code: FunChallengeErrorCode;
  message: string;
}

// ── Wire-shape helpers ───────────────────────────────────────────────────────

/**
 * The envelope every fun_* message carries. The `payload` string is
 * JSON.stringify(...) of one of the per-event shapes above.
 */
export interface FunChallengeEnvelope {
  requestId: string;
  type: string;
  gameType: string;
  id: string;
  payload: string;
}

/** Outbound `fun_room_create` payload. */
export interface FunRoomCreatePayload {
  inviteeIds: string[];
  creatorName: string;
  creatorAvatarUrl?: string;
  settings: ChallengeSettings;
}

/** Outbound `fun_move` payload. */
export interface FunMovePayload {
  correct: 0 | 1;
  square: string;
}

/** Outbound `fun_room_join` payload. */
export interface FunRoomJoinPayload {
  username?: string;
  avatarUrl?: string;
}

/** Inbound `fun_session_start` payload (server → client). */
export interface FunSessionStartPayload {
  sessionId: string;
  roomId: string;
  playerIds: string[];
  settings: ChallengeSettings;
  /**
   * Epoch ms (as string) when the 3-2-1 countdown finishes and moves start
   * being accepted server-side. The visible timerSec begins counting down
   * from this boundary, not from `endsAt - timerSec * 1000`. Optional for
   * back-compat with older cc-game-service builds that ship no countdown
   * — clients should default to "already started" when missing.
   */
  startsAt?: string;
  endsAt: string;            // server epoch ms as string
  coords: string[];
}

/** Inbound `fun_room_update` payload. */
export interface FunRoomUpdatePayload {
  players: Array<{ userId: string; username?: string; avatarUrl?: string }>;
  state: 'waiting' | 'in_session';
}

/** Inbound `fun_move_broadcast` payload. */
export interface FunMoveBroadcastPayload {
  playerId: string;
  correct: number;
  wrong: number;
  nextIndex: number;         // server-authoritative coord index for that player
  /**
   * The exact `square` payload the moving player sent on `fun_move` (for
   * Square Trainer it's the algebraic square; for PTB/PDB it's the
   * `"<puzzleId>:<uci>:<0|1>"` triple). Added so the moving player's
   * client can rebuild `myResponses` from the server echo instead of
   * mutating optimistically — see store.recordOpponentMove self-echo
   * branch. Older servers may omit this; the store treats it as optional.
   */
  square?: string;
}

/**
 * Position Trainer Battle: single puzzle row, hydrated from
 * GET /learning/puzzles/by_ids/ at battle start. `moves` is a
 * space-separated UCI line where index 0 is the opponent's setup move
 * (pushed onto the board so the player faces the puzzle position) and
 * subsequent plies alternate player / opponent.
 */
export interface BattlePuzzle {
  id: number;
  fen: string;
  /**
   * DEPRECATED — plaintext solution line. The SOLUTION is obfuscated on the
   * wire now (see Utils/puzzleEncrypter.ts): read it via
   * `resolvePuzzleMoves(puzzle)`, never this field directly. Kept only so
   * clients released before obfuscation shipped keep working; the server will
   * stop sending it once the active-version floor is past that build.
   */
  moves: string;
  /**
   * Obfuscated PLAYER answer plies ("v1:<base64>"). Absent when the server has
   * no key configured. Decode with `deobfuscatePuzzleMoves(token, puzzle.id)`
   * — the nonce is the id the client knows (the OFFSET id for curated demos).
   */
  moves_enc?: string;
  /**
   * The OPPONENT's auto-played first ply, in the clear — not a secret, it is
   * rendered on the board and highlighted as the last move. EMPTY for curated
   * `no_setup` puzzles, which have no setup ply and whose every ply is secret.
   */
  setup_ply?: string;
  rating: number;
  themes: string;
  /**
   * @deprecated No longer sent — it is a direct lookup key to the answer on
   * lichess.org, so shipping it would defeat the obfuscation. Retained on the
   * type only so any stale reader compiles; it will always be undefined.
   */
  lichess_puzzle_id?: string;
  /**
   * Classroom curated (Demo-Call) puzzles only. When true, the FEN is already
   * the PLAYER's turn and `moves` starts with the player's move — there is NO
   * auto-played opponent setup ply. The PositionTrainer walker branches on this
   * (plyIndex 0, orientation = FEN side-to-move). Absent/false for
   * cc_puzzles_global puzzles (which start with an opponent setup ply).
   */
  no_setup?: boolean;
  /**
   * Classroom curated puzzles only. A per-puzzle instructional hint; when
   * present the battle screen shows it in the bottom tip box instead of the
   * generic tip. Absent for cc_puzzles_global puzzles.
   */
  hint?: string;
}

/** Inbound `fun_session_rejoin` payload. */
export interface FunSessionRejoinPayload {
  sessionId: string;
  roomId: string;
  playerIds: string[];
  players: Array<{ userId: string; username?: string; avatarUrl?: string }>;
  settings: ChallengeSettings;
  /** See {@link FunSessionStartPayload.startsAt}. May be in the past when
   *  rejoining after the countdown finished. */
  startsAt?: string;
  endsAt: string;
  remainingMs: number;
  scores: Record<string, { correct: number; wrong: number }>;
  coords: string[];
  myResponses: string[];
  /**
   * Every player's response array (squares they clicked, in order). Used
   * client-side, together with `coords`, to reconstruct each player's
   * traffic-light move-history row on rejoin. Optional for back-compat
   * with older server builds.
   */
  allResponses?: Record<string, string[]>;
}
